import {useConfig} from '@aboutyou/config';
import {useIsFeatureActive} from '@aboutyou/feature-flags';
import {CampaignTopBanner} from '@aboutyou/grpc-clients/tadarida/aysa_api/entities/campaigns/v1/campaign_top_banner.types';
import {
  GetDiscountsResponse,
  GetDiscountsResponse_Discount,
  GetDiscountsResponse_Discount_PriceReductionCampaign,
} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/campaigns/v1/service.types';
import {useTranslation} from '@aboutyou/i18n';
import {TrackingPromotionCreative} from '@aboutyou/legacy-tracking-sdk';
import {useTrackPromotion} from '@aboutyou/tracking';
import {DiscountBanner as DiscountBannerBase} from '@aboutyou/ui-components';
import {UserGenders} from '@aboutyou/user';
import React, {useEffect} from 'react';
import {useSessionGender} from '../../../shared-deprecated/sessionGender';
import {useGetActiveDiscountFromDiscounts} from '../SelectedDiscounts/useSelectedDiscount';
import {
  isAppInstallIncentiveCampaign,
  isSimpleCampaign,
} from '../discountsHelper';
import {useGetGenderBanner} from '../hooks/useGetGenderBanner';

export interface DiscountBannerViewProps {
  className?: string;
  reducedSize?: boolean;
  onClick?: () => void;
  hideSimpleCampaign?: boolean;
  showOnlySimpleCampaign?: boolean;
}

const SetCssVariableForReducedHeight = () => {
  // need to wrap it into useEffect as its failing for desktop SSR
  useEffect(() => {
    document.body.style.setProperty(
      '--quick-view-discount-banner-height',
      '36px',
    );

    return () => {
      document.body.style.removeProperty('--quick-view-discount-banner-height');
    };
  }, []);

  return null;
};

const SetCssVariableForHeight = () => {
  // need to wrap it into useEffect as its failing for desktop SSR
  useEffect(() => {
    document.body.style.setProperty('--discount-banner-height', '50px');

    return () => {
      document.body.style.removeProperty('--discount-banner-height');
    };
  }, []);

  return null;
};

const DiscountBanner = ({
  className,
  activeDiscount,
  onClick,
  reducedSize,
  asSimpleBanner,
}: {
  className?: string;
  activeDiscount?: GetDiscountsResponse_Discount;
  onClick?: VoidFunction;
  reducedSize?: boolean;
  asSimpleBanner?: boolean;
}) => {
  const getGenderBanner = useGetGenderBanner();

  if (!activeDiscount) {
    return null;
  }

  switch (activeDiscount.discount?.$case) {
    case 'personalDiscount': {
      const discount = activeDiscount.discount.personalDiscount;
      return (
        <DiscountBannerBase
          className={className}
          reducedSize={reducedSize}
          title={discount.headlineText}
          endDate={discount.validUntil}
          headlineText={discount.headlineText}
          conditionText={discount.conditionText}
          link={activeDiscount.linkTarget?.url}
          onClick={onClick}
        />
      );
    }

    case 'priceReductionCampaign': {
      const discount = getGenderBanner(activeDiscount);
      if (!discount) {
        return null;
      }

      const isAppOnlyDiscount = isAppInstallIncentiveCampaign(
        activeDiscount.discount.priceReductionCampaign.campaign,
      );

      return (
        <>
          <TrackBannerPromotion
            genderDiscount={discount}
            activeDiscount={activeDiscount.discount.priceReductionCampaign}
          />
          <DiscountBannerBase
            className={className}
            reducedSize={reducedSize}
            asSimpleBanner={asSimpleBanner}
            title={discount.headlineText}
            endDate={
              discount.showCountdown
                ? activeDiscount.discount.priceReductionCampaign.campaign.period
                    .endDate
                : undefined
            }
            headlineText={discount.headlineText}
            conditionText={discount.conditionText}
            // this should be a BE behaviour, but doing it in FE as the bug  time-sensitive
            link={
              isAppOnlyDiscount ? undefined : activeDiscount.linkTarget?.url
            }
            backgroundColor={discount.backgroundColor}
            onClick={onClick}
          />
        </>
      );
    }
    default:
      return null;
  }
};

export const DiscountBannerView = ({
  className,
  discounts,
  reducedSize,
  onClick,
  hideSimpleCampaign,
  showOnlySimpleCampaign,
}: GetDiscountsResponse & DiscountBannerViewProps) => {
  const currentDiscount = useGetActiveDiscountFromDiscounts(discounts);
  const {isActive: isBasHugoTakeoverActive} = useIsFeatureActive(
    'bas_homepage_campaign_hugo',
  );
  const {device} = useConfig();
  const {gender} = useSessionGender();
  const {t} = useTranslation();

  if (!discounts?.length && !isBasHugoTakeoverActive) {
    return null;
  }

  const simpleCampaign = discounts.find(
    discount =>
      discount.discount?.$case === 'priceReductionCampaign' &&
      (isSimpleCampaign(discount.discount.priceReductionCampaign.campaign) ||
        isAppInstallIncentiveCampaign(
          discount.discount.priceReductionCampaign.campaign,
        )),
  );
  const activeDiscount = showOnlySimpleCampaign
    ? simpleCampaign
    : currentDiscount ?? simpleCampaign;

  const isSimpleBannerActive = simpleCampaign != null;

  if (
    isBasHugoTakeoverActive &&
    device === 'desktop' &&
    activeDiscount?.discount?.$case !== 'personalDiscount'
  ) {
    return (
      <DiscountBannerBase
        className={className}
        reducedSize={reducedSize}
        asSimpleBanner
        title={t(
          'Ready to explore HUGO Blue? More fashion, more attitude & more denim.',
        )}
        link={gender === UserGenders.Male ? '/s/hugo-106028' : '/s/hugo-106027'}
        backgroundColor={'#003594'}
        onClick={onClick}
      />
    );
  }

  if (isSimpleBannerActive && reducedSize) {
    // If the banner is reduced size and it's of simple type, we want to hide it to avoid showing both
    // in the quick view (reducedSize) and on the header
    return null;
  }

  if (isSimpleBannerActive && hideSimpleCampaign) {
    // If the active discount is a simple banner and we want to hide this from the banner
    // we should not return anything
    return null;
  }

  return (
    <>
      {isSimpleBannerActive && <SetCssVariableForHeight />}
      {reducedSize && <SetCssVariableForReducedHeight />}
      <DiscountBanner
        className={className}
        asSimpleBanner={isSimpleBannerActive}
        reducedSize={reducedSize}
        activeDiscount={activeDiscount}
        onClick={onClick}
      />
    </>
  );
};

const TrackBannerPromotion = ({
  genderDiscount,
  activeDiscount,
}: {
  genderDiscount: CampaignTopBanner;
  activeDiscount: GetDiscountsResponse_Discount_PriceReductionCampaign;
}) => {
  const trackPromotion = useTrackPromotion();

  // eslint-disable-next-line local-rules/too-complex-useeffects
  useEffect(() => {
    trackPromotion({
      promotion: {
        campaign: {
          name: activeDiscount.campaign.campaignName,
          id: activeDiscount.campaign.campaignId,
          destinationId: genderDiscount.path.split('?')[0]?.split('-').pop(),
          path: genderDiscount.path,
          linkedContent: 'top_banner',
        },
        source: TrackingPromotionCreative.Banner,
      },
      streamPosition: 0,
      actionType: 'impression',
    });
  }, []);

  return null;
};
